import React, { useEffect } from 'react';

import { Loading, Modal, Select, SelectOption, Text, Toggle } from '@components/common';
import { CopyURLButton } from '@components/shared/CopyURLButton';
import { BankSVG, GlobeSVG } from 'components/svgs';

export interface ShareModalProps {
  shareUrl: string;
  access: ShareAccess;
  scrub_pii: boolean;
  info?: string;
  isLoading: boolean;
  onClose: () => void;
  onOpen?: () => void;
  onCopyLink?: (url: string) => void;
  onUpdateAccess?: (access: ShareAccess) => void;
  onUpdateScrubPii?: (scrub_pii: boolean) => void;
  readOnly?: boolean;
}

const ACCESS_OPTIONS: SelectOption<ShareAccess>[] = [
  {
    value: 'private',
    label: 'Anyone on the account can edit'
  },
  {
    value: 'public',
    label: 'Anyone with the link can view'
  }
];

const ICONS: Record<ShareAccess, React.FC<React.PropsWithChildren<unknown>>> = {
  private: BankSVG,
  public: GlobeSVG
};

const Label: React.FC<React.PropsWithChildren<{ value: ShareAccess; label: string }>> = ({ value, label }) => {
  const Icon = ICONS[value];
  return (
    <div className='flex flex-row items-center space-x-2'>
      <Icon />
      <Text h='400'>{label}</Text>
    </div>
  );
};

export const ShareModal: React.FC<React.PropsWithChildren<ShareModalProps>> = ({
  shareUrl,
  access,
  scrub_pii,
  info,
  isLoading,
  onClose,
  onOpen,
  onCopyLink,
  onUpdateAccess,
  onUpdateScrubPii,
  readOnly
}) => {
  function onCopy() {
    onCopyLink?.(shareUrl);
  }

  useEffect(() => {
    onOpen?.();
  }, []);

  return (
    <Modal
      onClose={onClose}
      title='Share'
      size='md'
      renderFooter={() => (
        <div className='flex flex-row justify-between'>
          <CopyURLButton icon='link' primary text={shareUrl} onCopy={onCopy}>
            Copy link
          </CopyURLButton>
        </div>
      )}
    >
      {isLoading && <Loading absolute />}
      <Text bold h='600' mb='2'>
        Select privacy settings
      </Text>
      {info && (
        <Text color='gray-500' mb='6'>
          {info}
        </Text>
      )}
      <div className='mb-6 flex flex-row items-center justify-between'>
        <Select<ShareAccess>
          disabled={readOnly}
          options={ACCESS_OPTIONS}
          value={access}
          onChange={onUpdateAccess}
          renderLabel={(item) => <Label {...item} />}
          renderSelected={(item) => <Label {...item} />}
        />
      </div>
      {onUpdateScrubPii && (
        <div className='flex flex-row items-center space-x-2'>
          <Toggle testId='scrub-pii' on={scrub_pii} onToggle={onUpdateScrubPii} />
          <Toggle disabled={readOnly} on={scrub_pii} onToggle={onUpdateScrubPii} />
          <Text>Blur any video & hide Personally Identifiable Information (PII)</Text>
        </div>
      )}
    </Modal>
  );
};
